import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { PaymentErrorScreen } from '../../payment-gateways/core/components/PaymentErrorScreen';
import type { PaymentError } from '../../payment-gateways/core/utils/errors';
import { GatewayHost } from './GatewayHost';
import { resolveEntryOrder } from './resolveEntryOrder';

/**
 * Entry point reached from the booking app's redirect. Replaces
 * Payment.jsx + IndiaPayment.js + ThailandPayment.js.
 *
 * Its whole job is the fork: read the entry link once
 * (resolveEntryOrder.ts), then render either the error screen or the
 * gateway host (GatewayHost.tsx). Every branch below returns a screen —
 * there is no path here that falls through to a blank page.
 */
export default function PaymentPage() {
  const navigate = useNavigate();
  const resolution = useMemo(
    () =>
      resolveEntryOrder(
        new URLSearchParams(window.location.search),
        window.location.hostname
      ),
    []
  );
  // An error raised by a gateway after a good start (script blocked,
  // initiate-order unreachable) renders exactly like a bad entry link —
  // one error screen, not two.
  const [runtimeError, setRuntimeError] = useState<PaymentError | null>(null);

  const error = runtimeError ?? (resolution.ok ? null : resolution.error);

  if (error) {
    return (
      <PageShell>
        <PaymentErrorScreen error={error} onBack={() => navigate(-1)} />
      </PageShell>
    );
  }

  if (!resolution.ok) return null; // unreachable: `error` covers it above

  return (
    <PageShell>
      <GatewayHost order={resolution.order} onError={setRuntimeError} />
    </PageShell>
  );
}

function PageShell({ children }: { children: ReactNode }) {
  return (
    <div className="bg-app-gradient relative min-h-screen w-full">
      {children}
    </div>
  );
}
